import type { TimeOffEntry } from '../../../api/timeOff';

export interface TimeOffEntriesGridFilterOptions {
  showCancelled: boolean;
  showPast: boolean;
  now?: Date;
}

/** Whether the time-off window has fully ended (inclusive last calendar day for all-day). */
export function isTimeOffEntryEnded(
  entry: Pick<TimeOffEntry, 'endUtc' | 'isAllDay' | 'startUtc'>,
  now = new Date()
): boolean {
  if (!entry.endUtc) return false;
  const end = new Date(entry.endUtc);
  if (Number.isNaN(end.getTime())) return false;

  if (!entry.isAllDay) {
    return end.getTime() <= now.getTime();
  }

  const inclusiveEnd = new Date(end.getTime() - 24 * 60 * 60 * 1000);
  const start = new Date(entry.startUtc);
  const lastDay = inclusiveEnd >= start ? inclusiveEnd : end;
  const todayUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const lastDayUtc = Date.UTC(lastDay.getUTCFullYear(), lastDay.getUTCMonth(), lastDay.getUTCDate());
  return todayUtc > lastDayUtc;
}

export function shouldShowTimeOffEntryInGrid(
  entry: TimeOffEntry,
  options: TimeOffEntriesGridFilterOptions
): boolean {
  if (entry.status === 'Cancelled' && !options.showCancelled) return false;
  if (isTimeOffEntryEnded(entry, options.now) && !options.showPast) return false;
  return true;
}

export function shouldShowTimeOffEntryForCalendarTargets(
  entry: TimeOffEntry,
  activeTargetIds: Set<string>
): boolean {
  if (activeTargetIds.size === 0) return true;

  const links = entry.calendarLinks ?? [];
  if (links.length === 0) {
    return true;
  }

  return links.some((link) => activeTargetIds.has(link.calendarTargetId));
}
